import { User } from '../../userprofile/types/user'
import { analytics } from '../../utils/analytics'
import { Monitoring } from '../../utils/monitoring'

export const onNotLogged = (login: any) => {
  return async (error: Error) => {
    Monitoring.error(error, { source_url: window.location.href })
    await analytics.track('SSO - Redirected', { source_url: window.location.href, error: error?.message })
    await login()
  }
}

const RETURN_PATH_AFTER_RE_LOGIN_KEY = 'returnPathAfterReLogin'

export const saveReturnPathBeforeLogout = (): void => {
  try {
    const path = window.location.pathname + window.location.search
    if (path && path !== '/') {
      sessionStorage.setItem(RETURN_PATH_AFTER_RE_LOGIN_KEY, path)
    }
  } catch {
    // ignore storage errors
  }
}

export const getAndClearReturnPath = (): string | null => {
  try {
    const path = sessionStorage.getItem(RETURN_PATH_AFTER_RE_LOGIN_KEY)
    sessionStorage.removeItem(RETURN_PATH_AFTER_RE_LOGIN_KEY)
    if (!path) return null
    // Always return path-only. If a full URL was stored, extract pathname + search
    // so history.replace() doesn't double the origin (origin + pathname).
    if (path.startsWith('http')) {
      try {
        return new URL(path).pathname + new URL(path).search
      } catch {
        return null
      }
    }
    return path.startsWith('/') ? path : null
  } catch {
    return null
  }
}

export const onUnexpected = (logout: any) => {
  return async (error: Error) => {
    Monitoring.error(error, { source_url: window.location.href })
    await analytics.track('User - Sign in fail', { source_url: window.location.href, error: error?.message })

    try {
      saveReturnPathBeforeLogout()
      logout({ logoutParams: { returnTo: window.ENV.REACT_APP_URL } })
    } catch (e) {
      Monitoring.error(new Error(String(e)), {
        kind: 'logout',
      })
    }
  }
}

export const onTokenExpired = (logout: any) => {
  return async (error: Error) => {
    Monitoring.error(error, { source_url: window.location.href })
    await analytics.track('User - Signed Out', { source_url: window.location.href, error: error?.message })

    try {
      const language = localStorage.getItem('language') || ''
      saveReturnPathBeforeLogout()
      logout({ logoutParams: { returnTo: window.ENV.REACT_APP_URL } })
      localStorage.setItem('language', language)
    } catch (e) {
      Monitoring.error(new Error(String(e)), {
        kind: 'logout',
      })
    }
  }
}

const SESSION_EXPIRED_REDIRECT_AT_KEY = 'sessionExpiredRedirectAt'
// B2C signs a user with a live SSO cookie straight back in, so a token the API keeps rejecting
// (clock skew, issuer misconfiguration) would bounce between the app and login forever.
const SESSION_EXPIRED_REDIRECT_COOLDOWN_MS = 60_000

const redirectedRecently = (): boolean => {
  try {
    const at = Number(sessionStorage.getItem(SESSION_EXPIRED_REDIRECT_AT_KEY))
    return at > 0 && Date.now() - at < SESSION_EXPIRED_REDIRECT_COOLDOWN_MS
  } catch {
    return false
  }
}

const markRedirect = (): void => {
  try {
    sessionStorage.setItem(SESSION_EXPIRED_REDIRECT_AT_KEY, String(Date.now()))
  } catch {
    // ignore storage errors
  }
}

/**
 * Returns whether a login redirect is under way. It is not when the session already expired
 * right after the previous redirect; the caller then reports the failure as before.
 */
export const createSessionExpiredHandler = (login: () => Promise<void> | void) => {
  let redirecting = false

  return (operationName: string): boolean => {
    // Many operations fail together once a session ends; only the first one redirects.
    if (redirecting) return true

    if (redirectedRecently()) {
      Monitoring.event('Session expired redirect suppressed', {
        component: 'auth',
        start: Date.now(),
        meta: { operation: operationName },
      })
      return false
    }

    redirecting = true
    markRedirect()

    analytics.track('User - Session Expired', { source_url: window.location.href, operation: operationName })
    Monitoring.event('Session expired', {
      component: 'auth',
      start: Date.now(),
      meta: { operation: operationName },
    })
    saveReturnPathBeforeLogout()
    login()
    return true
  }
}

export const transformAzureUserToUser = async (idTokenClaims: any): Promise<User> => {
  return {
    type: 'logged',
    username: idTokenClaims.email || '',
    token: idTokenClaims.idToken || '',
    email: idTokenClaims.email || '',
    givenName: idTokenClaims.given_name || '',
    familyName: idTokenClaims.family_name || '',
    picture: idTokenClaims.picture || '',
    sub: idTokenClaims.sub,
    groupId: idTokenClaims.groups || [],
  }
}

export const validateAzureUser = async (idTokenClaims: any): Promise<any> => {
  if (!idTokenClaims || !idTokenClaims.sub) {
    throw new Error('Invalid ID token claims')
  }
  return idTokenClaims
}

export const matchAuthenticationErrors =
  (handlers: {
    noLogged: (error: Error) => void
    tokenExpired: (error: Error) => void
    unexpected: (error: Error) => void
  }) =>
  async (error: Error) => {
    if (error.message.includes('no_logged')) {
      Monitoring.error(error, { source_url: window.location.href })
      await analytics.track('SSO - Redirected', { source_url: window.location.href, error: error?.message })
      handlers.noLogged(error)
    } else if (error.message.includes('token_expired')) {
      Monitoring.error(error, { source_url: window.location.href })
      await analytics.track('User - Signed Out', { source_url: window.location.href, error: error?.message })

      try {
        const language = localStorage.getItem('language') || ''
        handlers.tokenExpired(error)
        localStorage.setItem('language', language)
      } catch (e) {
        Monitoring.error(new Error(String(e)), {
          kind: 'logout',
        })
      }
    } else {
      Monitoring.error(error, { source_url: window.location.href })
      await analytics.track('User - Sign in fail', { source_url: window.location.href, error: error?.message })

      try {
        handlers.unexpected(error)
      } catch (e) {
        Monitoring.error(new Error(String(e)), {
          kind: 'logout',
        })
      }
    }
  }
